import React from "react";

import {cn} from "@/lib/utils";

export const INSERTED_ITEM_MOTION_ENTER_DURATION_MS = 260;
export const INSERTED_ITEM_MOTION_HIGHLIGHT_DURATION_MS = 1600;

type Props = {
    active?: boolean;
    children: React.ReactNode;
    className?: string;
    highlightClassName?: string;
    contentClassName?: string;
    enterDurationMs?: number;
    highlightDurationMs?: number;
    playwrightId?: string;
};

export default function InsertedItemMotion({
    active = false,
    children,
    className,
    highlightClassName,
    contentClassName,
    enterDurationMs = INSERTED_ITEM_MOTION_ENTER_DURATION_MS,
    highlightDurationMs = INSERTED_ITEM_MOTION_HIGHLIGHT_DURATION_MS,
    playwrightId,
}: Props) {
    const contentRef = React.useRef<HTMLDivElement | null>(null);
    const frameRef = React.useRef<number | null>(null);
    const settleTimeoutRef = React.useRef<number | null>(null);
    const [isEntered, setIsEntered] = React.useState(!active);
    const [maxHeight, setMaxHeight] = React.useState<number | undefined>(active ? 0 : undefined);

    const clearScheduledWork = React.useCallback(() => {
        if (frameRef.current != null) {
            window.cancelAnimationFrame(frameRef.current);
            frameRef.current = null;
        }
        if (settleTimeoutRef.current != null) {
            window.clearTimeout(settleTimeoutRef.current);
            settleTimeoutRef.current = null;
        }
    }, []);

    React.useLayoutEffect(() => {
        clearScheduledWork();

        if (!active) {
            setIsEntered(true);
            setMaxHeight(undefined);
            return;
        }

        const measureHeight = () => {
            const node = contentRef.current;
            if (!node) {
                return 1;
            }

            const scrollHeight = node.scrollHeight || 0;
            const rectHeight = node.getBoundingClientRect().height || 0;
            return Math.max(scrollHeight, rectHeight, 1);
        };

        setIsEntered(false);
        setMaxHeight(0);

        frameRef.current = window.requestAnimationFrame(() => {
            frameRef.current = window.requestAnimationFrame(() => {
                setMaxHeight(measureHeight());
                setIsEntered(true);
            });
        });

        settleTimeoutRef.current = window.setTimeout(() => {
            setMaxHeight(undefined);
            settleTimeoutRef.current = null;
        }, enterDurationMs);

        return clearScheduledWork;
    }, [active, clearScheduledWork, enterDurationMs]);

    React.useEffect(() => {
        return clearScheduledWork;
    }, [clearScheduledWork]);

    return (
        <div
            className={cn(
                "overflow-hidden motion-reduce:transition-none",
                className,
            )}
            style={{
                maxHeight: maxHeight == null ? undefined : `${maxHeight}px`,
                transitionProperty: "max-height",
                transitionDuration: `${enterDurationMs}ms`,
                transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
            }}
            data-playwright-id={playwrightId}
            data-inserted-item-active={active ? "true" : "false"}
            data-inserted-item-entered={isEntered ? "true" : "false"}
        >
            <div
                className={cn(
                    "motion-reduce:transition-none",
                    isEntered ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-1.5",
                    contentClassName,
                )}
                style={{
                    transitionProperty: "opacity, transform",
                    transitionDuration: `${enterDurationMs}ms`,
                    transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
                }}
            >
                <div
                    ref={contentRef}
                    className={cn(
                        "rounded-[2rem] motion-reduce:transition-none",
                        active
                            ? "ring-1 ring-primary/25 shadow-[var(--surface-shadow-soft)]"
                            : "ring-0 ring-transparent shadow-none",
                        highlightClassName,
                    )}
                    style={{
                        transitionProperty: "box-shadow",
                        transitionDuration: `${highlightDurationMs}ms`,
                        transitionTimingFunction: "ease-out",
                    }}
                >
                    {children}
                </div>
            </div>
        </div>
    );
}
